import {
  DashboardHeader,
  WhiteCard,
  UiButton,
  UiCardSearchInput,
  UiBadge,
} from "@/components";
import DashboardLayout from "@/components/layouts/dashboard-layout";
import { useCommande } from "@/composables/commande";
import { Plus, Eye, Edit, OctagonPause } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ReactElement, useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

interface QRData {
  matricule: string;
  matriculeCrypte: string;
}

const qrDatas: QRData[] = [
  { matricule: "F-DR-678A1-F4", matriculeCrypte: "abc123" },
  { matricule: "F-DR-678A1-F5", matriculeCrypte: "def456" },
  { matricule: "F-DR-678A1-F6", matriculeCrypte: "ghi789" },
  { matricule: "F-DR-678A1-F7", matriculeCrypte: "abc123rrr" },
  { matricule: "F-DR-678A1-F8", matriculeCrypte: "def456èy" },
  { matricule: "F-DR-678A1-F9", matriculeCrypte: "ghi789yt" },
  { matricule: "F-DR-678A1-FA", matriculeCrypte: "ghi789yt" },
  { matricule: "F-DR-678A1-FB", matriculeCrypte: "ghi789yt" },
  { matricule: "F-DR-678A1-FC", matriculeCrypte: "ghi789yt" },
  { matricule: "F-DR-678A1-FD", matriculeCrypte: "ghi789yt" },
];

export default function DashboardCommandes() {
  const navigate = useNavigate();
  const { commandes, fetchCommandes } = useCommande();

  const pdfContentRef = useRef<HTMLDivElement>(null);
  const [qrPerRow, setQrPerRow] = useState(3); // Nombre de QR codes par ligne
  const [imageLoaded, setImageLoaded] = useState(false);
  const [image, setImage] = useState<string | null>(null);

  useEffect(() => {
    const loadImage = async () => {
      const imageUrl =
        "https://upload.wikimedia.org/wikipedia/commons/f/fd/Flag_of_Senegal.svg"; // Remplacez par l'URL de votre image
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], "senegal-flag.png", { type: blob.type });
      setImage(URL.createObjectURL(file));
      setImageLoaded(true); // Indique que l'image est chargée
    };

    loadImage();
  }, []);

  const generatePDF = () => {
    if (!imageLoaded) {
      alert("Veuillez attendre que toutes les images soient chargées.");
      return;
    }

    if (!pdfContentRef.current) {
      alert("Erreur: élément de contenu non trouvé.");
      return;
    }

    html2canvas(pdfContentRef.current).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      const imgWidth = 190;
      const pageHeight = pdf.internal.pageSize.height;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      pdf.save("qrcodes.pdf");
    });
  };

  const columns = [
    {
      field: "auteur",
      headerName: "Auteur",
    },
    {
      field: "nom",
      headerName: "Nom",
    },
    {
      field: "typeCampagne",
      headerName: "Type",
    },
    {
      field: "region",
      headerName: "Region",
    },
    {
      field: "objectif",
      headerName: "Objectif",
    },
    {
      field: "status",
      headerName: "Statut",
      valueGetter: (row: any) => {
        return <UiBadge variant={`terminate`.toLowerCase()}>Terminé</UiBadge>;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      valueGetter: (row: any) => {
        return (
          <div className="flex gap-2 flex-wrap">
            <UiButton
              size="small"
              onClick={() => navigate(`/dashboard/campagne/${row.id}`)}
            >
              <Eye />
            </UiButton>
            <UiButton
              size="small"
              onClick={() => navigate(`/dashboard/campagne/${row.id}`)}
            >
              <Edit />
            </UiButton>
            <UiButton size="small" onClick={() => {}}>
              <OctagonPause />
            </UiButton>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <DashboardHeader subTitle="Bienvenue aux campagnes" />

      <div className="py-4">
        <WhiteCard
          styles={{ borderRadius: "30px" }}
          headerSlot={
            <div className="flex items-center justify-between">
              <UiButton
                onClick={() => navigate("/dashboard/commandes/add")}
                startIcon={<Plus />}
              >
                Générer les boucles
              </UiButton>

              <div>
                <UiCardSearchInput
                  onSubmit={() => {}}
                  placeholder="Recherche"
                />
              </div>
            </div>
          }
        >
          <div className="py-6">
            <div>
              <h1>Générateur de QR Codes</h1>
              <div>
                <label>
                  Nombre de QR Codes par ligne:
                  <input
                    type="number"
                    value={qrPerRow}
                    onChange={(e: any) =>
                      setQrPerRow(Math.max(1, e.target.value))
                    }
                    style={{ width: "50px", marginLeft: "10px" }}
                  />
                </label>
              </div>
              <button onClick={generatePDF} style={{ marginTop: "20px" }}>
                Télécharger en PDF
              </button>
              <div
                ref={pdfContentRef}
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${qrPerRow}, 1fr)`,
                  gap: "10px",
                  marginTop: "20px",
                }}
              >
                {qrDatas.map((item: QRData, index: number) => (
                  <div
                    key={index}
                    style={{
                      border: "1px solid black",
                      padding: "1rem",
                      marginTop: "1.2rem",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ marginBottom: "15px" }}>
                      {image && ( // Vérifiez si l'image est chargée
                        <img
                          src={image}
                          alt="Drapeau du Sénégal"
                          style={{ width: "50px", height: "30px" }}
                        />
                      )}
                      <div
                        style={{
                          fontSize: "7px",
                          fontWeight: "bold",
                          margin: "0",
                          padding: "0",
                        }}
                      >
                        UN PEUPLE - UN BUT - UNE FOIE
                      </div>
                    </div>

                    <div style={{ marginBottom: "10px" }}>
                      <QRCodeSVG
                        value={`http://fff/animal/${item.matriculeCrypte}`}
                        size={100}
                      />
                    </div>
                    <div
                      style={{
                        marginTop: "10px",
                        marginBottom: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      {item.matricule}
                    </div>
                    <div style={{ fontSize: "7px", margin: "0", padding: "0" }}>
                      Ministère de l'agriculture et de l'élévage | JOURGUI.SN
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={generatePDF} style={{ marginTop: "20px" }}>
                Télécharger en PDF
              </button>
            </div>
          </div>
        </WhiteCard>
      </div>
    </>
  );
}
